/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-06 14:28:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import {
  Badge,
  SkeletonDisplayText,
  SkeletonBodyText,
  TextStyle,
  Card,
  Button
} from '@shopify/polaris';
import {
  formatPriceDisplay,
  getCorrectPlanV4,
  buildDiscountInvoiceV4,
  getDiscountCodeValue
} from 'src/helpers/subscription-v3-helper';
import { formatNumberInThousand } from 'src/helpers';
import RenderNoticeStatus from 'src/components/views/subscription-v4/render-plan-notice-status';
import AddonRow from 'src/components/views/subscription-v4/addon/addon-row';
import CostBreakdownModal from 'src/components/views/subscription-v4/modal/cost-breakdown-modal';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import { PLAN_STATUS } from 'src/constants/subscription';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import { allowToShowBFCM23 } from 'src/helpers/dashboard-helper';
import StarsIcon from 'src/assets/images/common/stars-2x.svg';
import { enableNewPricingPageExceededUser } from 'src/helpers/subscription-v3-helper';

//--------------------------
// Plan Summary
const PlanSummary = props => {
  // props
  const {
    i18n,
    appLanguage,
    gettingInvoice,
    gettingPackage,
    currentTotalProducts = 0,
    planInfo = {},
    packageInfo = {},
    correctPlan = {},
    subscription = {},
    addonsAdded = [],
    nextBillingDate,
    unsubscribing,
    tenantApp,
    handleUnsubscribe,
    onOpenFeedback,
    discountVerified = {},
    clearDiscountCode,
    subscribe,
    host,
    confirmationUrl,
    verifying,
    verifyDiscountCode,
    remainCycles,
    invoice,
    msgVerifyError,
    termsConditions,
    getUnsubscribeAddOnInfo,
    getAddons,
    trialExpiresAt,
    getDetailInvoice
  } = props;
  const enableForExceededUser = enableNewPricingPageExceededUser(tenantApp);

  // state
  const [currentInvoiceData, setCurrentInvoiceData] = useState({});
  const [costBreakdown, setCostBreakdown] = useState(false);

  // Get correct name plan
  const { name: planName = 'basic' } = correctPlan;
  const packageName = planName.split('_')[0];

  // Get today
  const [todayDateString] = new Date().toISOString().split('T');
  const today = new Date(todayDateString).getTime() / 1000;

  // Discount info
  const { discountCodeApplied = {} } = planInfo;
  const { dcValue, dcPostfixValue } = getDiscountCodeValue(discountCodeApplied);

  const expiredAt = _.get(discountCodeApplied, 'expiredAt', 0);
  const { totalPrice = 0 } = currentInvoiceData;

  const { status = '' } = subscription;
  const isTrialUser = ['trial', 'expiredSoon', 'expired'].includes(status);

  // useEffect

  useEffect(() => {
    const currentInvoiceData = buildDiscountInvoiceV4(
      planInfo,
      packageInfo,
      discountVerified,
      i18n
    );
    setCurrentInvoiceData(currentInvoiceData);
  }, [planInfo, discountVerified]);

  // Redirect to shopify confirmation page
  useEffect(() => {
    if (confirmationUrl) {
      window.top.location.href = confirmationUrl;
      return;
    }
  }, [confirmationUrl]);

  const bfcm23 = allowToShowBFCM23(tenantApp);
  const conditionBFCM23 = !isTrialUser && bfcm23.allow && bfcm23.type !== 'professional365';
  const conditionWithoutBFCM23 = !isTrialUser && !conditionBFCM23 && !enableForExceededUser;

  return (
    <>
      <Card
        title={
          <div className="cardTitle">
            <span className="Polaris-Heading">
              {i18n.translate('AdminHTML.SubscriptionV4.summary')}
            </span>
          </div>
        }>
        <Card.Section>
          <div className="BC-planSummary">
            <div className="status">
              {/* Current status */}
              <div>
                <TextStyle variation="strong">
                  {!(gettingInvoice && gettingPackage) ? (
                    i18n.translate(
                      `AdminHTML.DashboardV3.SubscriptionStatus.packages.${packageName}`
                    )
                  ) : (
                    <div style={{ textAlign: 'center', width: '10%' }}>
                      <SkeletonDisplayText size="small" />
                    </div>
                  )}
                </TextStyle>
                {/* Render badges status */}
                {gettingInvoice ? (
                  <div style={{ textAlign: 'center', width: '10%' }}>
                    <SkeletonDisplayText size="small" />
                  </div>
                ) : (
                  <div>
                    {renderStatus(props)}

                    {/* Render discount value badge */}
                    {dcValue && (expiredAt > today || !expiredAt) ? (
                      <span style={{ marginLeft: '10px' }}>
                        <Badge status="info">
                          <span className="title">
                            {i18n.translate(
                              'AdminHTML.SubscriptionV4.PlanStatus.discountPercentBadge',
                              { nt_value: dcPostfixValue }
                            )}
                          </span>
                        </Badge>
                      </span>
                    ) : (
                      ''
                    )}
                  </div>
                )}
              </div>
              {/* Render notice status */}
              {gettingInvoice ? (
                <div style={{ margin: '16px auto', textAlign: 'center' }}>
                  <SkeletonBodyText lines={1} />
                </div>
              ) : (
                <div className="bottom">
                  <RenderNoticeStatus
                    currentInvoiceData={currentInvoiceData}
                    discountCodeApplied={discountCodeApplied}
                    packageInfo={packageInfo}
                    subscription={subscription}
                    tenantApp={tenantApp}
                  />
                </div>
              )}
            </div>
            <div className="productCount">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SubscriptionV4.Packages.productCount')}
              </TextStyle>
              <div className="count">{formatNumberInThousand(currentTotalProducts)}</div>
            </div>
            <div className="invoiceTotal">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SubscriptionV4.PlanStatus.invoiceTotal')}
              </TextStyle>
              {gettingInvoice ? (
                <div style={{ textAlign: 'center', width: '10%' }}>
                  <SkeletonDisplayText size="small" />
                </div>
              ) : (
                <div className="count">
                  ${formatPriceDisplay(totalPrice)}
                  <span style={{ fontSize: '14px', lineHeight: '20px' }}>
                    {i18n.translate('AdminHTML.SubscriptionV4.Common.perMonth')}
                  </span>
                </div>
              )}
              <div className="cost" onClick={() => setCostBreakdown(true)}>
                {i18n.translate('AdminHTML.SubscriptionV4.PlanStatus.seeCostBreakDown')}
              </div>
              <CostBreakdownModal
                active={costBreakdown}
                handleCancel={() => setCostBreakdown(false)}
                status={status}
                discountVerified={discountVerified}
                clearDiscountCode={clearDiscountCode}
                subscribe={subscribe}
                host={host}
                planInfo={planInfo}
                packageInfo={packageInfo}
                verifying={verifying}
                remainCycles={remainCycles}
                invoice={invoice}
                verifyDiscountCode={verifyDiscountCode}
                msgVerifyError={msgVerifyError}
                termsConditions={termsConditions}
                tenantApp={tenantApp}
              />
            </div>
          </div>
          <div>
            {conditionBFCM23 && (
              <div className="bfcm23-plan">
                <Button
                  primary
                  onClick={() => {
                    history.push(routes.subscription.package());
                  }}>
                  <img src={StarsIcon} alt="stars" style={{ marginRight: '4px' }} />
                  {i18n.translate(
                    'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text2'
                  )}
                </Button>
              </div>
            )}
            {conditionWithoutBFCM23 && (
              <Button onClick={() => history.push(routes.subscription.package())} primary>
                {i18n.translate('AdminHTML.SubscriptionV4.ActionButton.upgradePlan')}
              </Button>
            )}
          </div>
        </Card.Section>
        {addonsAdded.length > 0 && (
          <Card.Section>
            <div className="planSummary-addon">
              <div className="title">
                {i18n.translate('AdminHTML.SubscriptionV4.PlanStatus.addon')}
              </div>
              <div className="addons">
                {addonsAdded.map((addon, indexRow) => (
                  <AddonRow
                    key={indexRow}
                    addon={addon}
                    nextBillingDate={nextBillingDate}
                    unsubscribing={unsubscribing}
                    handleUnsubscribe={handleUnsubscribe}
                    onOpenFeedback={onOpenFeedback}
                    unsubscribeAddOnInfo={getUnsubscribeAddOnInfo(addon)}
                    getAddons={getAddons}
                    getDetailInvoice={getDetailInvoice}
                    appLanguage={appLanguage}
                    storeStatus={status}
                    trialExpiresAt={trialExpiresAt}
                  />
                ))}
              </div>
            </div>
          </Card.Section>
        )}
      </Card>
    </>
  );
};

// Sub - components
// Render status badge
const renderStatus = props => {
  const { i18n, status = '' } = props;

  const infoStatus = PLAN_STATUS.find(e => e.status === status);
  const { label = '', type = '' } = infoStatus;
  return (
    <Badge status={type}>
      <span className="title">{i18n.translate(label)}</span>
    </Badge>
  );
};
//---------------------
const mapDispatchToProps = (dispatch, ownProps) => ({
  subscribe: (planId, code, host, addons) =>
    dispatch(subscriptionV3Actions.subscribeV4(planId, code, host, addons, ownProps.i18n)),
  clearDiscountCode: () => dispatch(subscriptionV3Actions.clearDiscountCode()),
  verifyDiscountCode: (code, subscribeOn, addOns, newPlanName) =>
    dispatch(subscriptionV3Actions.verifyDiscountCode(code, subscribeOn, addOns, newPlanName)),
  getAddons: () => dispatch(subscriptionV3Actions.getAddons(ownProps.i18n)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n))
});
const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts');
  const selectedPlan = _.get(state.oauth.tenantApp, 'selectedPlan', '');
  // Get correct plan
  const correctPlan = getCorrectPlanV4(
    chargeInfo,
    packagePlans,
    currentTotalProducts,
    selectedPlan
  );
  const host = _.get(state.oauth, 'host', '');

  return {
    host,
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    discountVerified: _.get(state.subscriptionV3, 'discount', {}),
    msgVerifyError: _.get(state.subscriptionV3, 'msgError', null),
    termsConditions: _.get(state.subscriptionV3, 'termsConditions', ''),
    currentTotalProducts,
    verifying: _.get(state.subscriptionV3, 'verifying', false),
    remainCycles: _.get(state.subscriptionV3, 'remainCycles', 0),
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    gettingInvoice: _.get(state.subscriptionV3, 'gettingInvoice', false),
    gettingPackage: _.get(state.subscriptionV3, 'gettingPackage', false),
    tenantApp,
    subscription: _.get(state.oauth, 'subscription', {}),
    trialExpiresAt: _.get(tenantApp, 'trialExpiresAt', 0),
    confirmationUrl: _.get(state.subscriptionV3, 'confirmationUrl', ''),
    correctPlan
  };
};
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PlanSummary));
